import { AggregateBase } from "csi/action/AggregateBase";
import { ControllerInterface } from "csi/action/Controller";
import CsiFormData from "csi/action/FormData";
import { GoToDialogSave } from "csi/components/select/store/SelectStoreBase";
import config from "csi/config";
import { QRY_LIMIT_PATH } from "csi/constants";
import { Action } from "csi/def/Action";
import { Actions } from "csi/def/Actions";
import ContextConstructor, { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import { View } from "csi/def/View";
import ActionData from "csi/heading/ActionData";
import AggregateAttribute from "csi/heading/AggregateAttribute";
import StringRen from "csi/heading/String";
import html from "csi/html";
import locale from "csi/locale";
import GoToDialog, { GoToDialogProps } from "csi/middleware/goToDialog/GoToDialog";
import GoToDialogStore, { IGoToDialogGroup, IGoToDialogTab } from "csi/middleware/goToDialog/GoToDialogStore";
import { getCustomViewContainerKeyForType } from "csi/model/customView";
import { DojoViewProps } from "csi/page/DojoView";
import reflection from "csi/reflection";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import { resolveNode, resolveNodes } from "csi/service/nodeService";
import { getClassDefaultTableView } from "csi/service/viewService";
import WorkingCache from "csi/state/WorkingCache";
import { routerStore } from "csi/state/rootStore";
import AggregateGroupNodes, { IAggregateGroupValues, getAggregateGroupKey } from "csi/store/AggregateGroupNodes";
import StoreCache from "csi/store/Cache";
import { StoreNames } from "csi/store/Names";
import { INVALID_PROPERTY } from "csi/store/common";
import TablePreferencesConstructor from "csi/table/Preferences";
import { TableView } from "csi/table/View";
import { TableWidget } from "csi/table/Widget";
import { ICache } from "csi/typings";
import { getDialogZIndex } from "csi/utility/htmlUtility";
import { QueryLevel } from "csi/view/_query/Level";
import Tooltip from "csi/widget/Tooltip";
import declare from "dojo/_base/declare";
import _ from "lodash";
import { IAggregateStoreGroup, IAggregateStoreResponse, IAggregateStoreResult } from "src/c8-platform/type/aggregateStore";
import { ATTR_NAME, C8Node, C8NodeType } from "src/c8-platform/type/node";
import { cnl } from "src/c8-platform/type/value";

export type GoToDialogTableFunction = (tableView: TableView, nodeType: C8NodeType) => void;
export type TabResource = [type: string, res: string];

/** build form data */
export function buildFormData(
	action?: Action,
	cache?: ICache,
	node?: C8Node,
	parent?: C8Node,
	context?: Context,
): CsiFormData {
	const ctx = context || new ContextConstructor();
	const formAction = action || new AggregateAttribute();
	const formCache = cache || new WorkingCache();
	const source = {
		getAction: () => formAction,
		getCache: () => formCache,
		getNode: () => node,
		getParent: () => parent,
		getContext: () => ctx,
	};
	const formData = new CsiFormData(source);
	return formData;
}

/** return true only if there is one group in response; otherwise there are suggestions that override any unenforced user defaults */
export function canApplyUnenforcedUserDefaults({ groups }: IAggregateStoreResponse): boolean {
	return groups.length === 1;
}

/** return true if two aggregate store groups can appear in the same view */
export function canConsolidateAggregateStoreGroups(group1: IAggregateStoreGroup, group2: IAggregateStoreGroup): boolean {
	return group1.nodeType === group2.nodeType &&
		group1.dataStoreInfo.pathHintsId === group2.dataStoreInfo.pathHintsId &&
		!group1.limited && !group2.limited;
}

const _GroupPreferences = declare(TablePreferencesConstructor, {
	constructor() {
		this.removeCustomOption("groupSettings");
	},
});

const _GroupRenderer = declare(StringRen, {
	/** add suggested tag to suggested groups */
	toHtmlStatic(ctx: Context, h: Heading, refNode: HTMLElement, actionData: ActionData): boolean {
		const qr = ctx.queryResult;
		const { rootValueStore } = qr.parentResult;
		const { suggestedProperty } = rootValueStore as IAggregateGroupValues;
		const storeItem = qr.parentResult.rootValueStore.get(qr.getGroupValue());
		const finished = this.inherited<boolean>(arguments);

		if (finished && storeItem?.[suggestedProperty]) {
			// need to split into two spans because first is using table-cell display which will span whole height of row rather than just what is needed for SUGGESTED text
			const span = html.create("span", {
				className: "csi-suggested-tag",
			}, refNode, "after");

			html.create("span", {
				innerText: locale.capitalizeText(locale.resource("Suggested")),
			}, span);
		}

		return finished;
	},
});

/** return properties needed to render a table view containing results of aggregate group */
export function canGroupAggregateStoreGroups(response: IAggregateStoreResponse, groups: IAggregateStoreGroup[]): boolean {
	const { requireText, unenforcedUserDefaultsApplied } = response;
	const { labelResource, limited } = groups[0];
	const multipleGroups = groups.length > 1;
	const dataStoreDriven = requireText || limited || unenforcedUserDefaultsApplied;
	return !dataStoreDriven && (multipleGroups || !!labelResource);
}

/** return properties needed to render a table view containing results of aggregate group */
export function convertAggregateStoreGroupToTableView(
	response: IAggregateStoreResponse,
	groups: IAggregateStoreGroup[],
	grouped: boolean,
	cacheView: ICache,
	formData: CsiFormData,
	dialogCardId?: string,
	subtypeName?: string,
	onAggregateStoreTableView?: GoToDialogTableFunction,
): DojoViewProps {
	const firstGroup = groups[0];
	const multipleGroups = groups.length > 1;
	const {
		dataStoreId,
		dataStoreInfo,
		dataStoreSource,
		dataStoreParams,
		limited,
		nodeType,
	} = firstGroup;

	const { requireText, unenforcedUserDefaultsApplied } = response;
	const { canGroup, pathHintsId, sortAttributes } = dataStoreInfo;
	const skipUnenforced = !canApplyUnenforcedUserDefaults(response);
	const dataStoreDriven = requireText || limited || unenforcedUserDefaultsApplied;
	const customViewContainerKey = getCustomViewContainerKeyForType(nodeType, subtypeName, pathHintsId, grouped);
	const resultUrls = getGroupsResultUrls(groups);
	const viewNodeType = reflection.TYPE_HOME;
	const nodeUrl = reflection.URL_HOME;
	const sourceNode = reflection.getNode(nodeUrl);
	const groupKey = getAggregateGroupKey(nodeType);

	// custom view context is set to dataStoreId if view is not grouped; this is important to help differentiate views of the same
	// type that are split apart, when for example using delayed search. The custom view container is the same, but they will have a
	// different context so they can operate independently. We don't base this decision on "requireText" because it makes more sense
	// to differentiate local custom views based on the data store id (context) rather than whether using delayed search.
	const prefViewContext = multipleGroups ? "" : getUniqueDataStoreIdPerResponse(response, firstGroup);

	// add root node of view to cache
	cacheView.add(sourceNode);

	// create view definition - use _CS_Data as source node since it can be viewed by all
	const defView: TableView = getClassDefaultTableView(reflection.getClass(nodeType), reflection.getClass(viewNodeType), pathHintsId, customViewContainerKey);
	defView.m_formData = formData;

	// add specific card if specified
	if (dialogCardId) {
		defView.m_headings = [
			Object.assign(defView.newHeading(), {
				m_bCard: true,
				m_id: dialogCardId,
			}),
		];
	}

	// alter store query and parameters
	const onCreateMainQueryLevel = (ql: QueryLevel): void => {
		// aggregate store operation applies enforced user defaults, so client does not need to
		ql.skipEnforcedUserDefaults = true;

		// skip unenforced user defaults in some cases
		ql.skipUnenforcedUserDefaults = skipUnenforced;

		if (dataStoreDriven) {
			ql.configureForDataStore(dataStoreId, dataStoreSource, dataStoreParams);
		} else {
			ql.requestParameters = {
				[QRY_LIMIT_PATH]: resultUrls,
			};

			if (grouped) {
				ql.storeClass = AggregateGroupNodes;
				ql.storeOptions = {
					aggregateStoreGroups: groups,
				};
			} else if (!resultUrls.length) {
				// if no results, then pass empty resultUrls array to store for it to be loaded and empty;
				// if this is not done, then limit paths will be empty and you will get all results rather than none
				ql.storeOptions = {
					resultUrls,
				};
			}
		}
	};

	// specify view parameters
	const viewParams: Partial<TableWidget> = {
		cacheView,
		customViewPathHints: pathHintsId,
		dataStoreDriven,
		dataStoreInfo: {
			...dataStoreInfo,

			// if combining groups, then default sorting is not applied and custom view grouping is disabled
			canGroup: !grouped && canGroup,
			sortAttributes: !grouped && sortAttributes,
		},
		defView,
		onCreateMainQueryLevel,
		prefViewContext,
	};

	if (grouped || !sortAttributes?.length) {
		// hard-code grouping rather than allow in custom views - the key does not really matter,
		// as the values returned from the server will be re-grouped by aggregate groups
		if (grouped) {
			viewParams.groupSettings = { key1: groupKey.typedString, expand1: true };
			viewParams.groupRendererClass = _GroupRenderer;
			viewParams.prefClass = _GroupPreferences;
		}

		// remove any sorting when grouped as it is possibly a combination of multiple data stores;
		// the user will be able to sort after information is displayed, but there won't be an indication
		// of what it is currently sorted by (we may be able to fix in future - see AggregateGroupNodes)
		const rel = defView.getPageRelationship();
		rel.bOrdered = true;
		rel.typeSortAttribute = undefined;
	}

	// call extension point to modify the view
	onAggregateStoreTableView?.(defView, nodeType);

	return {
		nodeUrl,
		viewId: defView.m_id,
		viewNodeType,
		viewParams,
	};
}

/** return view properties and tab resources to display based on aggregate store response */
export function getAggregateStoreTabs(
	aggregateStoreResponse: IAggregateStoreResponse,
	cacheView: ICache,
	formData: CsiFormData,
	rca?: ReflectionAttribute,
	dialogCardId?: string,
	onAggregateStoreTableView?: GoToDialogTableFunction,
): IGoToDialogTab[] {
	const { groups, requireText } = aggregateStoreResponse;
	const consolidatedGroups: IAggregateStoreGroup[][] = [];
	const { ordered, subtypeName } = rca || {};
	let lastTabGroupIndex = -1;

	// determine how many tabs we have, some of which could contain multiple groups;
	// If in delay search mode, then consolidating the groups is not possible since we do not have all results and need to generate a tab per group.
	// If not, then test consecutive groups to see if they are compatible.
	groups.forEach((group, i) => {
		if (!i || requireText || !canConsolidateAggregateStoreGroups(group, groups[i - 1])) {
			consolidatedGroups[++lastTabGroupIndex] = [group];
		} else {
			consolidatedGroups[lastTabGroupIndex].push(group);
		}
	});

	const tabs = consolidatedGroups.map((aggregateGroups) => {
		const grouped = canGroupAggregateStoreGroups(aggregateStoreResponse, aggregateGroups);
		const viewProps = convertAggregateStoreGroupToTableView(aggregateStoreResponse, aggregateGroups, grouped, cacheView, formData, dialogCardId, subtypeName, onAggregateStoreTableView);
		const groups = aggregateGroups.map((aggregateGroup) => {
			// results may only be partial if using delayed search
			const {
				groupId: id,
				nodeType,
				results = [],
			} = aggregateGroup;

			return {
				aggregateGroup,
				id,
				nodeType,
				results,
			};
		});

		return {
			dynamicResults: !!requireText,
			grouped,
			groups,
			ordered,
			viewProps,
		};
	});

	return tabs;
}

/** return list of all result urls across given groups */
function getGroupResultUrls(group: IAggregateStoreGroup): cnl[] {
	return group.results?.map(({ $id }) => $id);
}

/** return list of all result urls across given groups */
function getGroupsResultUrls(groups: IAggregateStoreGroup[]): cnl[] {
	return _.flatten(groups.map(getGroupResultUrls));
}

/** return list of invalid results from the Names store */
async function getInvalidResults(namesStore: StoreNames): Promise<IAggregateStoreResult[]> {
	const { labelProperty } = namesStore;
	const queryResults = await namesStore.asyncQuery();
	const items = Array.from(queryResults);
	const invalidItems = items.filter((item) => !!item[INVALID_PROPERTY]);

	return invalidItems.map((item) => ({
		$id: namesStore.getIdentity(item) as cnl,
		[ATTR_NAME]: item[labelProperty],
		LocalizedName: item[labelProperty],
	}));
}

/** return data store id with index as suffix if used more than once in the response; a data store can be used multiple times with different parameters */
function getUniqueDataStoreIdPerResponse({ groups }: IAggregateStoreResponse, selectedGroup: IAggregateStoreGroup): string {
	const { dataStoreId } = selectedGroup;
	let count = 1;
	let i = 0;

	while (groups[i] !== selectedGroup) {
		if (groups[i].dataStoreId === dataStoreId) {
			++count;
		}

		++i;
	}

	return `${dataStoreId}${(count > 1) ? count : ""}`;
}

/** open selection dialog */
export async function goToDialog(
	ac: ControllerInterface,
	rca?: ReflectionAttribute,
	forceAllowSave?: boolean,
	namesStore?: StoreNames,
	dialogCardId?: string,
	defView?: View,
	nodeUrl?: cnl,
	onAggregateStoreTableView?: GoToDialogTableFunction,
): Promise<void> {
	const { bodyComponent, openDialog, openInlineDialog } = routerStore;
	const cacheView = ac.m_cache;
	const invalidResults = !namesStore || namesStore.ignoreInvalid ? [] : await getInvalidResults(namesStore);
	const aggregateStoreResponse = namesStore?.aggregateStoreResponse;
	const saveDialogProps = ac.getReactSaveDialogProps();

	// ensure that namesStore is queried before calling getFormData as the aggregateStoreResponse contributes to the form data
	const formData = ac.getFormData();

	let legacyActions: Actions;
	let tabs: IGoToDialogTab[];

	if (defView) {
		// use pre-defined view for Go to Dialog
		tabs = getGoToDialogTabsFromView(defView, nodeUrl, [], ac, rca);

		// set legacy actions then clear proxy actions
		legacyActions = defView.m_defActions;
		defView.m_defActions = null;

		// force enable Save button if reverse aggregation action, but rca is not bi-directional
		const aggregateBase = ac.m_action as AggregateBase;

		if (!forceAllowSave && aggregateBase.bReverse && !rca?.bidirectionalRelation) {
			forceAllowSave = true;
		}
	} else if (aggregateStoreResponse) {
		const { disabledIds } = aggregateStoreResponse;

		// if there are disabled items, then add callback to form data indicating so
		if (disabledIds) {
			formData.m_fAggregateTest = (ctx): boolean => !disabledIds.has(ctx.getNodeURL());
		}

		// create views dynamically based on aggregate store response
		tabs = getAggregateStoreTabs(aggregateStoreResponse, cacheView, formData, rca, dialogCardId, onAggregateStoreTableView);
	} else {
		// should never happen since should be using view or aggregate store, but just in case
		console.error("Go to dialog only works with a view definition or an aggregate store");
		return;
	}

	const store = new GoToDialogStore(aggregateStoreResponse, cacheView, formData, invalidResults, tabs, !!forceAllowSave);
	const zIndex = getDialogZIndex();

	// if dialog contents are editable, then this dialog must be main dialog and leave inline dialog for inline editing within dialog
	console.assert(!bodyComponent || !formData.m_bEditable, "React dialog already in use");
	const dialogFunction = formData.m_bEditable ? openDialog : openInlineDialog;

	ac.useReactDialog(dialogFunction(GoToDialog, {
		legacyActions,
		saveDialogProps,
		store,
		zIndex,
	} as GoToDialogProps));
}

/**
 * open selection dialog without action controller from nodes
 * @param resultUrls - set of cnls for nodes to display in new viewParams
 * @param formData -  formData to use in new view
 * @param nodeType - type of the nodes in the view
 * @param nodeUrl - url of the location node
 * @param onSave - onSave function to use when save is clicked
 * @param cache - cache for store
 * @param multiSelect - boolean to determine if multiselect is being used in the view
 * @param forceAllowSave - optional, forces the dialog to always allow the dialog to be saved
 */
export function openSelectionDialogFromCNLs(
	resultUrls: cnl[],
	formData: CsiFormData,
	nodeType: C8NodeType,
	nodeUrl: cnl,
	onSave: VoidFunction,
	cache: ICache,
	multiSelect: boolean,
	forceAllowSave?: boolean,
): GoToDialogStore {
	const defView: TableView = getClassDefaultTableView(reflection.getClass(nodeType));
	defView.m_formData = formData;
	return openSelectionDialog(defView, nodeType, nodeUrl, onSave, cache, forceAllowSave, resultUrls, multiSelect);
}

/**
 * finishing the gotodialog store using viewParams from a parent
 * @param defView - view used
 * @param nodeType - type of the nodes in the view
 * @param nodeUrl - url of the location node
 * @param onSave - onSave function to use when save is clicked
 * @param cache - cache for store
 * @param forceAllowSave - optional, forces the dialog to always allow the dialog to be saved
 * @param resultUrls - optional, set of cnls for nodes to display in new viewParams if openSelectionDialogFromCNLs was used
 * @param multiSelect - boolean to determine if multiselect is being used in the view
 */
export function openSelectionDialog(
	defView: View,
	nodeType: C8NodeType,
	nodeUrl: cnl,
	onSave: VoidFunction,
	cache: ICache,
	forceAllowSave?: boolean,
	resultUrls: cnl[] = [],
	multiSelect?: boolean,
): GoToDialogStore | null {
	const tabs = getGoToDialogTabsFromView(defView, nodeUrl, resultUrls);
	const store = new GoToDialogStore(null, cache, defView.m_formData, [], tabs, !!forceAllowSave);
	// if multiselect we want to show the plural name instead
	const titleType = !multiSelect ? locale.type(nodeType) : locale.typePlural(nodeType);
	const saveDialogProps = {
		title: locale.resource("SelectSomething", [titleType]),
		onClose: (): void => {
			routerStore.closeInlineDialog();
		},
		onSave,
	};
	Tooltip.hide();

	routerStore.openInlineDialog(GoToDialog, {
		saveDialogProps,
		store,
		zIndex: getDialogZIndex(),
	});
	return store;
}

/** return Go to Dialog view properties from given view definition */
export function getGoToDialogTabsFromView(
	defView: View,
	nodeUrl: cnl,
	resultUrls: cnl[],
	ac?: ControllerInterface,
	rca?: ReflectionAttribute,
): IGoToDialogTab[] {
	// We overrode the query so that if the user applied ad-hoc table filters in the previous dialog, that they would not be reapplied in the next dialog.
	const query = {};

	// prepare parameters for view
	const viewParams: Record<string, any> = {
		defView,
		query,
	};

	// if there are resultUrls use those inside of viewParams as well
	if (resultUrls.length > 0) {
		const onCreateMainQueryLevel = (ql: QueryLevel): void => {
			ql.requestParameters = {
				[QRY_LIMIT_PATH]: resultUrls,
			};
		};
		viewParams.onCreateMainQueryLevel = onCreateMainQueryLevel;
	}
	const nodeType = defView.getPageClass()?.type;
	const viewId = defView.m_id;

	// determine subtype if ref attribute to class where subtype is required (i.e. LookupItem);
	// attribute may not have destination class if there are multiple, or attribute is type "string" with format "Selection"
	const subtypeUrl = rca?.subtypeName ? config.getSubtypeUrlByName(nodeType, rca.subtypeName) : null;

	ac?.preOpenDialog(defView, viewParams);

	const viewProps: DojoViewProps = {
		nodeUrl,
		onViewCreated: ac ? (view): void => ac._onCreatePopup(view) : (): void => null,
		subtypeUrl,
		viewId,
		viewParams,
	};

	const group: IGoToDialogGroup = {
		id: viewId,
		nodeType,
		results: [],
	};

	return [{
		dynamicResults: true,
		grouped: false,
		groups: [group],
		ordered: rca?.ordered,
		viewProps,
	}];
}

/** encapsulated version of goToDialog that uses the aggregate store */
export async function goToDialogAggregateStore(
	namesStore: StoreNames,
	nodeUrl: cnl,
	selectedUrls: cnl[] = [],
	invalidResults: IAggregateStoreResult[] = [],
	isMultiSelect: boolean = false,
	label: string,
	goToDialogSave?: GoToDialogSave,
): Promise<void> {
	const { aggregateStoreResponse, aggregateStoreParams } = namesStore;
	if (!aggregateStoreResponse || !aggregateStoreParams) {
		console.error("goToDialogAggregateStore: aggregateStoreResponse is null or aggregateStoreParams is null");
		return;
	}

	const { attributeId } = aggregateStoreParams;
	const action = new AggregateAttribute();
	action.aggregateId = attributeId;
	const cacheView = new StoreCache();
	const node = await resolveNode(nodeUrl, cacheView);
	// TODO: get these from the aggregate store selected values when update operation is supported
	const selectedNodes = await resolveNodes(selectedUrls, cacheView);
	const formData = buildFormData(action, cacheView, node);
	formData.m_bSelectOne = !isMultiSelect;
	selectedNodes.forEach((node) => formData.addAggregatedNode(node));
	const rca = reflection.getAttribute(node, attributeId);
	const tabs = getAggregateStoreTabs(aggregateStoreResponse, cacheView, formData, rca);
	const store = new GoToDialogStore(aggregateStoreResponse, cacheView, formData, invalidResults, tabs);
	const zIndex = getDialogZIndex();
	const title = locale.resource("SelectSomething", [label]);

	routerStore.setCenterFloatComponent(GoToDialog, {
		saveDialogProps: {
			onCancel: (): void => goToDialogSave?.(selectedUrls),
			onClose: (): void => routerStore.closeCenterFloatComponentDialog(),
			onSave: (): void => {
				const selectedUrls = formData.getSelectedValue();
				goToDialogSave?.(selectedUrls);
			},
			title,
		},
		store,
		zIndex,
	} as GoToDialogProps);
}
